export default {

  /**
   * Creates a new, empty Vector2D
   * @returns {Vector2D} a new 2D vector
   */
  create() {
    var out = [];
    out[0] = 0;
    out[1] = 0;
    return out;
  },

  /**
   * creates a new Vector2D initialized with values from an existing vector
   * @param {Vector2D} a vector to clone
   * @returns {Vector2D} a new 2D vector
   */
  clone(a) {
    var out = [];
    out[0] = a[0];
    out[1] = a[1];
    return out;
  },

  /**
   * creates a new Vector2D initialized with the given values
   * @param {Number} x X component
   * @param {Number} y Y component
   * @returns {Vector2D} a new 2D vector
   */
  fromValues(x, y) {
    var out = [];
    out[0] = x;
    out[1] = y;
    return out;
  },

  /**
   * copy the values from one Vector2D to another
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the source vector
   * @returns {Vector2D} out
   */
  copy(out, a) {
    out[0] = a[0];
    out[1] = a[1];
    return out;
  },

  /**
   * Set the components of a Vector2D to the given values
   *
   * @param {Vector2D} out the receiving vector
   * @param {Number} x X component
   * @param {Number} y Y component
   * @returns {Vector2D} out
   */
  set(out, x, y) {
    out[0] = x;
    out[1] = y;
    return out;
  },

  /**
   * Adds two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  add(out, a, b) {
    out[0] = a[0] + b[0];
    out[1] = a[1] + b[1];
    return out;
  },

  /**
   * Subtracts vector b from vector a
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  subtract(out, a, b) {
    out[0] = a[0] - b[0];
    out[1] = a[1] - b[1];
    return out;
  },


  /**
   * Multiplies two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  multiply(out, a, b) {
    out[0] = a[0] * b[0];
    out[1] = a[1] * b[1];
    return out;
  },

  /**
   * Divides two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  divide(out, a, b) {
    out[0] = a[0] / b[0];
    out[1] = a[1] / b[1];
    return out;
  },

  /**
   * Returns the minimum of two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  min(out, a, b) {
    out[0] = Math.min(a[0], b[0]);
    out[1] = Math.min(a[1], b[1]);
    return out;
  },

  /**
   * Returns the maximum of two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Vector2D} out
   */
  max(out, a, b) {
    out[0] = Math.max(a[0], b[0]);
    out[1] = Math.max(a[1], b[1]);
    return out;
  },

  /**
   * Scales a Vector2D by a scalar number
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the vector to scale
   * @param {Number} b amount to scale the vector by
   * @returns {Vector2D} out
   */
  scale(out, a, b) {
    out[0] = a[0] * b;
    out[1] = a[1] * b;
    return out;
  },

  /**
   * Adds two Vector2D's after scaling the second operand by a scalar value
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @param {Number} scale the amount to scale b by before adding
   * @returns {Vector2D} out
   */
  scaleAndAdd(out, a, b, scale) {
    out[0] = a[0] + (b[0] * scale);
    out[1] = a[1] + (b[1] * scale);
    return out;
  },

  /**
   * Calculates the euclidian distance between two Vector2D's
   *
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Number} distance between a and b
   */
  distance(a, b) {
    var x = b[0] - a[0];
    var y = b[1] - a[1];
    return Math.sqrt(x * x + y * y);
  },

  /**
   * Calculates the squared euclidian distance between two Vector2D's
   *
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Number} squared distance between a and b
   */
  squaredDistance(a, b) {
    var x = b[0] - a[0];
    var y = b[1] - a[1];
    return x * x + y * y;
  },

  /**
   * Calculates the length of a Vector2D
   *
   * @param {Vector2D} a vector to calculate length of
   * @returns {Number} length of a
   */
  length(a) {
    var x = a[0];
    var y = a[1];
    return Math.sqrt(x * x + y * y);
  },

  /**
   * Calculates the squared length of a Vector2D
   *
   * @param {Vector2D} a vector to calculate squared length of
   * @returns {Number} squared length of a
   */
  squaredLength(a) {
    var x = a[0];
    var y = a[1];
    return x * x + y * y;
  },

  /**
   * Negates the components of a Vector2D
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a vector to negate
   * @returns {Vector2D} out
   */
  negate(out, a) {
    out[0] = -a[0];
    out[1] = -a[1];
    return out;
  },

  /**
   * Negates the components of a Vector2D
   *
   * @param {Vector2D} out the receiving vector
   * @param {Integer} a limit
   * @returns {Vector2D} out
   */
  limit(out, a, b) {
    var x = a[0],
      y = a[1];
    var len = (x * x) + (y * y);
    if (len > (b * b) && len > 0) {
      out[0] = x;
      out[1] = y;
      this.normalize(out, out);
      this.scale(out, out, b);
    }
    return out;
  },


  /**
   * Normalize a Vector2D
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a vector to normalize
   * @returns {Vector2D} out
   */
  normalize(out, a) {
    var x = a[0];
    var y = a[1];
    var len = x * x + y * y;
    if (len > 0) {
      len = 1 / Math.sqrt(len);
      out[0] = a[0] * len;
      out[1] = a[1] * len;
    }
    return out;
  },

  /**
   * Calculates the dot product of two Vector2D's
   *
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {Number} dot product of a and b
   */
  dot(a, b) {
    return a[0] * b[0] + a[1] * b[1];
  },

  /**
   * Computes the cross product of two Vector2D's
   * Note that the cross product must by definition produce a 3D vector
   *
   * @param {vec3} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @returns {vec3} out
   */
  cross(out, a, b) {
    var z = a[0] * b[1] - a[1] * b[0];
    out[0] = out[1] = 0;
    out[2] = z;
    return out;
  },

  /**
   * Performs a linear interpolation between two Vector2D's
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the first operand
   * @param {Vector2D} b the second operand
   * @param {Number} t interpolation amount between the two inputs
   * @returns {Vector2D} out
   */
  lerp(out, a, b, t) {
    var ax = a[0];
    var ay = a[1];
    out[0] = ax + t * (b[0] - ax);
    out[1] = ay + t * (b[1] - ay);
    return out;
  },

  /**
   * Generates a random vector with the given scale
   *
   * @param {Vector2D} out the receiving vector
   * @param {Number} [scale] Length of the resulting vector. If ommitted, a unit vector will be returned
   * @returns {Vector2D} out
   */
  random(out, scale) {
    scale = scale || 1.0;
    var r = Math.random() * 2.0 * Math.PI;
    out[0] = Math.cos(r) * scale;
    out[1] = Math.sin(r) * scale;
    return out;
  },

  /**
   * Transforms the Vector2D with a mat2
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the vector to transform
   * @param {mat2} m matrix to transform with
   * @returns {Vector2D} out
   */
  transformMat2(out, a, m) {
    var x = a[0];
    var y = a[1];
    out[0] = m[0] * x + m[2] * y;
    out[1] = m[1] * x + m[3] * y;
    return out;
  },

  /**
   * Transforms the Vector2D with a mat2d
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the vector to transform
   * @param {mat2d} m matrix to transform with
   * @returns {Vector2D} out
   */
  transformMat2d(out, a, m) {
    var x = a[0];
    var y = a[1];
    out[0] = m[0] * x + m[2] * y + m[4];
    out[1] = m[1] * x + m[3] * y + m[5];
    return out;
  },

  /**
   * Transforms the Vector2D with a mat3
   * 3rd vector component is implicitly '1'
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the vector to transform
   * @param {mat3} m matrix to transform with
   * @returns {Vector2D} out
   */
  transformMat3(out, a, m) {
    var x = a[0];
    var y = a[1];
    out[0] = m[0] * x + m[3] * y + m[6];
    out[1] = m[1] * x + m[4] * y + m[7];
    return out;
  },

  /**
   * Transforms the Vector2D with a mat4
   * 3rd vector component is implicitly '0'
   * 4th vector component is implicitly '1'
   *
   * @param {Vector2D} out the receiving vector
   * @param {Vector2D} a the vector to transform
   * @param {mat4} m matrix to transform with
   * @returns {Vector2D} out
   */
  transformMat4(out, a, m) {
    var x = a[0];
    var y = a[1];
    out[0] = m[0] * x + m[4] * y + m[12];
    out[1] = m[1] * x + m[5] * y + m[13];
    return out;
  },

  /**
   * Returns a string representation of a vector
   *
   * @param {Vector2D} vec vector to represent as a string
   * @returns {String} string representation of the vector
   */
  str(a) {
    return 'Vector2D(' + a[0] + ', ' + a[1] + ')';
  }

}
